import { useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import api from "../helpers/api";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CssBaseline from "@mui/material/CssBaseline";
import FormLabel from "@mui/material/FormLabel";
import FormControl from "@mui/material/FormControl";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import MuiCard from "@mui/material/Card";
import { styled } from "@mui/material/styles";
import AppTheme from "../shared-theme/AppTheme";
import IconButton from "@mui/material/IconButton";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";

const Card = styled(MuiCard)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignSelf: "center",
  width: "100%",
  padding: theme.spacing(4),
  gap: theme.spacing(2),
  margin: "auto",
  boxShadow:
    "hsla(220, 30%, 5%, 0.05) 0px 5px 15px 0px, hsla(220, 25%, 10%, 0.05) 0px 15px 35px -5px",
  [theme.breakpoints.up("sm")]: {
    width: "450px",
  },
  ...theme.applyStyles("dark", {
    boxShadow:
      "hsla(220, 30%, 5%, 0.5) 0px 5px 15px 0px, hsla(220, 25%, 10%, 0.08) 0px 15px 35px -5px",
  }),
}));

const GradientBackground = styled("div")({
  position: "fixed",
  inset: 0,
  background: "radial-gradient(circle at top left, #041C2C 0%, #22143C 100%)",
  zIndex: -1,
});

const SettingsContainer = styled(Stack)(({ theme }) => ({
  minHeight: "100dvh",
  padding: theme.spacing(2),
  overflowY: "auto",
  position: "relative",
  [theme.breakpoints.up("sm")]: {
    padding: theme.spacing(4),
  },
}));

export default function ResetPassword() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");

  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [isSuccess, setIsSuccess] = useState(false);
  const [passwordErrors, setPasswordErrors] = useState<string[]>([]);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const validatePassword = (pass: string, confirmPass: string) => {
    const errors = [];
    if (pass.length < 8) errors.push("Password must be at least 8 characters");
    if (!/[A-Z]/.test(pass))
      errors.push("Password must contain an uppercase letter");
    if (!/[a-z]/.test(pass))
      errors.push("Password must contain a lowercase letter");
    if (!/\d/.test(pass)) errors.push("Password must contain a number");
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(pass))
      errors.push("Password must contain a special character");
    if (confirmPass && pass !== confirmPass)
      errors.push("Passwords do not match");
    setPasswordErrors(errors);
    return errors.length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setMessage("");

    if (!validatePassword(newPassword, confirmPassword)) {
      return;
    }

    setLoading(true);
    try {
      await api.put("/user/passwordreset", {
        token,
        updatedPassword: newPassword,
      });
      setMessage("Password successfully updated");
      setIsSuccess(true);
    } catch (err) {
      setError("Failed to reset password. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AppTheme>
      <CssBaseline enableColorScheme />
      <GradientBackground />
      <SettingsContainer direction="column" justifyContent="space-between">
        <Card variant="outlined">
          <Typography
            component="h2"
            variant="h4"
            sx={{ width: "100%", fontSize: "clamp(2rem, 10vw, 2.15rem)" }}
          >
            Password Reset
          </Typography>

          {!isSuccess ? (
            <Box
              component="form"
              onSubmit={handleSubmit}
              sx={{ display: "flex", flexDirection: "column", gap: 2 }}
            >
              <FormControl>
                <FormLabel htmlFor="new-password">New Password</FormLabel>
                <TextField
                  required
                  fullWidth
                  id="new-password"
                  type={showNewPassword ? "text" : "password"}
                  value={newPassword}
                  onChange={(e) => {
                    setNewPassword(e.target.value);
                    validatePassword(e.target.value, confirmPassword);
                  }}
                  variant="outlined"
                  InputProps={{
                    endAdornment: (
                      <IconButton
                        size="small"
                        disableRipple
                        aria-label="toggle new password visibility"
                        onClick={() => setShowNewPassword(!showNewPassword)}
                        edge="end"
                        sx={{ bgcolor: "transparent", border: "none" }}
                      >
                        {showNewPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    ),
                  }}
                />
              </FormControl>

              <FormControl>
                <FormLabel htmlFor="confirm-password">
                  Confirm Password
                </FormLabel>
                <TextField
                  required
                  fullWidth
                  id="confirm-password"
                  type={showConfirmPassword ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => {
                    setConfirmPassword(e.target.value);
                    validatePassword(newPassword, e.target.value);
                  }}
                  variant="outlined"
                  InputProps={{
                    endAdornment: (
                      <IconButton
                        size="small"
                        disableRipple
                        aria-label="toggle confirm password visibility"
                        onClick={() =>
                          setShowConfirmPassword(!showConfirmPassword)
                        }
                        edge="end"
                        sx={{ bgcolor: "transparent", border: "none" }}
                      >
                        {showConfirmPassword ? (
                          <VisibilityOff />
                        ) : (
                          <Visibility />
                        )}
                      </IconButton>
                    ),
                  }}
                />
              </FormControl>

              {passwordErrors.length > 0 && (
                <ul style={{ color: "red", fontSize: "0.8em", margin: 0 }}>
                  {passwordErrors.map((error, index) => (
                    <li key={index}>{error}</li>
                  ))}
                </ul>
              )}

              {error && <Typography color="error.main">{error}</Typography>}

              <Button
                type="submit"
                fullWidth
                variant="contained"
                disabled={
                  loading ||
                  !newPassword ||
                  !confirmPassword ||
                  passwordErrors.length > 0
                }
                sx={{
                  "&.Mui-disabled": {
                    color: "black",
                    opacity: 0.7,
                    backgroundColor: "primary.main",
                  },
                }}
              >
                Update Password
              </Button>
            </Box>
          ) : (
            <Box sx={{ textAlign: "center" }}>
              <Typography>{message}</Typography>
              <Button
                fullWidth
                variant="contained"
                onClick={() => navigate("/login")}
                sx={{ mt: 2 }}
              >
                Return to Login
              </Button>
            </Box>
          )}
        </Card>
      </SettingsContainer>
    </AppTheme>
  );
}
